// src/common/services/serviceFactory.js
// Factory pattern for creating service instances.
// Switches between real API services and mock services based on environment.
// Mock services use in-memory data with simulated delays and always successful outcomes.
// Interfaces defined with JSDoc for type safety and consistency.

import { isDemoMode } from '../config/env';
import axios from 'axios';
import { initialMockData } from '../mock/initialData';
import { getCurrentTimestamp } from '../mock/initialData'; // Reuse utility

// Base API config for real services
const API_BASE = '/api'; // Relative for Next.js API routes
const getAuthHeaders = (token) => token ? { Authorization: `Bearer ${token}` } : {};

// Simulated delay for mock operations (500-1500ms for realism)
const simulateDelay = () => new Promise(resolve => setTimeout(resolve, Math.random() * 1000 + 500));

// Persistence key for demo data
const PERSISTENCE_KEY = 'milyTravelDemoData';

// Base Real Service Class (for API calls)
class BaseRealService {
 constructor(endpoint) {
   this.endpoint = `${API_BASE}/${endpoint}`;
 }

 async getAll(token) {
   try {
     const response = await axios.get(this.endpoint, { headers: getAuthHeaders(token) });
     return response.data;
   } catch (error) {
     throw new Error(`Failed to fetch: ${error.message}`);
   }
 }

 async getById(id, token) {
   try {
     const response = await axios.get(`${this.endpoint}/${id}`, { headers: getAuthHeaders(token) });
     return response.data;
   } catch (error) {
     throw new Error(`Failed to fetch item: ${error.message}`);
   }
 }

 async create(data, token) {
   try {
     const response = await axios.post(this.endpoint, data, { headers: getAuthHeaders(token) });
     return response.data;
   } catch (error) {
     throw new Error(`Failed to create: ${error.message}`);
   }
 }

 async update(id, data, token) {
   try {
     const response = await axios.patch(`${this.endpoint}/${id}`, { id, updateDTO: data }, { headers: getAuthHeaders(token) });
     return response.data;
   } catch (error) {
     throw new Error(`Failed to update: ${error.message}`);
   }
 }

 async delete(id, token) {
   try {
     await axios.delete(`${this.endpoint}/${id}`, { headers: getAuthHeaders(token) });
     return { success: true, id };
   } catch (error) {
     throw new Error(`Failed to delete: ${error.message}`);
   }
 }
}

// Base Mock Service Class (in-memory with relationships, always success, persistent)
class BaseMockService {
  constructor(dataKey) {
    console.log('[DEBUG MOCK SERVICE] Constructor called with dataKey:', dataKey);
    this.dataKey = dataKey;
    this.loadFromStorage();
  }

 loadFromStorage() {
   if (typeof window === 'undefined') return; // No storage on server
   try {
     const stored = localStorage.getItem(PERSISTENCE_KEY);
     console.log('[DEBUG MOCK SERVICE] Loading from storage, key:', PERSISTENCE_KEY, 'Stored data exists:', !!stored);
     if (stored) {
       const parsed = JSON.parse(stored);
       console.log('[DEBUG MOCK SERVICE] Parsed stored data for', this.dataKey, ':', parsed[this.dataKey] ? parsed[this.dataKey].length : 0);
       this.data = parsed[this.dataKey] || initialMockData[this.dataKey];
       // Ensure relationships: merge with initial if missing keys
       this.data.forEach(item => {
         if (!item.createdAt) item.createdAt = getCurrentTimestamp();
         if (!item.updatedAt) item.updatedAt = getCurrentTimestamp();
       });
     } else {
       console.log('[DEBUG MOCK SERVICE] No stored data, using initial for', this.dataKey, 'length:', initialMockData[this.dataKey].length);
       this.data = [...initialMockData[this.dataKey]];
     }
     console.log('[DEBUG MOCK SERVICE] Final data loaded for', this.dataKey, 'length:', this.data.length);
   } catch (error) {
     console.log("[DEBUG MOCK SERVICE] Persistence load error:", error);
     console.log('[DEBUG MOCK SERVICE] Fallback to initial for', this.dataKey, 'length:', initialMockData[this.dataKey].length);
     this.data = [...initialMockData[this.dataKey]];
   }
 }

 saveToStorage() {
   if (typeof window === 'undefined') return;
   try {
     const allData = JSON.parse(localStorage.getItem(PERSISTENCE_KEY) || '{}');
     allData[this.dataKey] = this.data;
     localStorage.setItem(PERSISTENCE_KEY, JSON.stringify(allData));
   } catch (error) {
     console.log("Persistence save error:", error);
   }
 }

 async getAll() {
   console.log('[DEBUG MOCK SERVICE] getAll called for', this.dataKey, 'data length before filter:', this.data.length);
   await simulateDelay();
   const filtered = this.data.filter(item => item.isActive !== false);
   console.log('[DEBUG MOCK SERVICE] getAll returning', filtered.length, 'active items for', this.dataKey);
   return filtered;
 }

 async getById(id) {
   await simulateDelay();
   const item = this.data.find(item => item._id === id);
   if (!item) throw new Error(`Item not found: ${id}`);
   return { ...item }; // Return copy
 }

 async create(newItem) {
   await simulateDelay();
   const id = generateId(); // From mock utils
   const createdItem = { ...newItem, _id: id, createdAt: getCurrentTimestamp(), updatedAt: getCurrentTimestamp() };
   this.data.unshift(createdItem); // Add to front for recent first
   this.saveToStorage();
   // Simulate cascading: e.g., update related user balance if needed (business logic)
   this.handleCascadingUpdates(createdItem);
   return createdItem;
 }

 async update(id, updateData) {
   await simulateDelay();
   const index = this.data.findIndex(item => item._id === id);
   if (index === -1) throw new Error(`Item not found: ${id}`);
   const updatedItem = { ...this.data[index], ...updateData, updatedAt: getCurrentTimestamp() };
   this.data[index] = updatedItem;
   this.saveToStorage();
   // Simulate cascading updates (e.g., recalculate totals)
   this.handleCascadingUpdates(updatedItem);
   return updatedItem;
 }

 async delete(id) {
   await simulateDelay();
   const index = this.data.findIndex(item => item._id === id);
   if (index === -1) throw new Error(`Item not found: ${id}`);
   this.data[index].isActive = false; // Soft delete for relationships
   this.saveToStorage();
   // Simulate cascading: remove from related arrays (e.g., user.receivers)
   this.handleCascadingDeletes(id);
   return { success: true, id };
 }

 // Placeholder for business logic: cascading updates/deletes
 handleCascadingUpdates(item) {
   // Override in specific services for complex relationships
   // e.g., For combos, update stock; for users, recalculate balance
 }

 handleCascadingDeletes(id) {
   // Override for relationships, e.g., remove receiver from user.receivers
 }
}

// Utility for ID generation (from mock)
const generateId = () => Math.random().toString(36).substr(2, 9);

// Specific Services

// Auth Service (special handling for demo: any credentials succeed)
export class AuthService {
  /**
   * Interface for AuthService
   * @typedef {Object} AuthMethods
   * @property {function} login - Login user
   * @property {function} register - Register user
   * @property {function} forgotPassword - Send recovery email
   */

  constructor() {
    this.isMock = isDemoMode();
  }

  async login(credentials) {
    if (this.isMock) {
      await simulateDelay();
      // Always success in demo: find or create user based on role
      let user = initialMockData.users.find(u => u.email === credentials.email && u.role === credentials.role);

      if (!user) {
        // If user not found with specific role, try to find by email and update role
        user = initialMockData.users.find(u => u.email === credentials.email);
        if (user) {
          user.role = credentials.role; // Update role for existing user
          console.log(`[DEBUG MOCK AUTH] User ${user.email} role updated to ${user.role}`);
        } else {
          // Simulate auto-register with the selected role
          user = {
            _id: generateId(),
            email: credentials.email,
            fullName: credentials.email.split('@')[0],
            role: credentials.role, // Use the selected role
            password: 'hashed_' + credentials.password,
            isVerified: true,
            balance: { USD: 100 },
            receivers: [],
            createdAt: getCurrentTimestamp(),
            updatedAt: getCurrentTimestamp(),
          };
          initialMockData.users.push(user); // Add to mock data
          console.log(`[DEBUG MOCK AUTH] New user ${user.email} created with role ${user.role}`);
        }
      } else {
        console.log(`[DEBUG MOCK AUTH] User ${user.email} found with role ${user.role}`);
      }

      const token = generateId() + '.' + generateId(); // Simulated JWT
      localStorage.setItem('accessToken', token); // Persist token
      localStorage.setItem('currentUser', JSON.stringify(user));
      return { user, token, success: true };
    } else {
      // Real API login
      try {
        const response = await axios.post(`${API_BASE}/auth/login`, credentials);
        const { user, token } = response.data;
        localStorage.setItem('accessToken', token);
        localStorage.setItem('currentUser', JSON.stringify(user));
        return { user, token, success: true };
      } catch (error) {
        throw new Error('Login failed');
      }
    }
  }

  async register(userData) {
    if (this.isMock) {
      await simulateDelay();
      const newUser = { ...userData, _id: generateId(), role: 'user', isVerified: true, balance: { USD: 0 }, receivers: [], createdAt: getCurrentTimestamp(), updatedAt: getCurrentTimestamp() };
      initialMockData.users.push(newUser);
      const token = generateId() + '.' + generateId();
      localStorage.setItem('accessToken', token);
      localStorage.setItem('currentUser', JSON.stringify(newUser));
      return { user: newUser, token, success: true };
    } else {
      // Real register
      const response = await axios.post(`${API_BASE}/auth/register`, userData);
      const { user, token } = response.data;
      localStorage.setItem('accessToken', token);
      localStorage.setItem('currentUser', JSON.stringify(user));
      return { user, token, success: true };
    }
  }

  async forgotPassword(email) {
    if (this.isMock) {
      await simulateDelay();
      // Simulate email sent
      return { success: true, message: 'Email de recuperación enviado a ' + email };
    } else {
      const response = await axios.post(`${API_BASE}/auth/forgot-password`, { email });
      return response.data;
    }
  }

  logout() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('currentUser');
  }
}

// User Service
export class UserService extends (isDemoMode() ? BaseMockService : BaseRealService) {
  constructor() {
    super(isDemoMode() ? 'users' : 'users');
  }

  handleCascadingUpdates(user) {
    // e.g., Recalculate balance if transactions, but for demo, simple
    if (user.balance) {
      // Simulate business logic
      user.balance.totalCup = Object.values(user.balance).reduce((a, b) => a + b, 0);
    }
  }
}

// Combo Service
export class ComboService extends (isDemoMode() ? BaseMockService : BaseRealService) {
  constructor() {
    console.log('[DEBUG COMBO SERVICE] Constructor called, demo mode:', isDemoMode(), 'Super class:', isDemoMode() ? 'BaseMockService' : 'BaseRealService');
    super(isDemoMode() ? 'combos' : 'combos');
  }

  async getAll(token) {
    console.log('[DEBUG COMBO SERVICE] getAll called with token:', !!token, 'demo mode:', isDemoMode());
    const result = await super.getAll(token);
    console.log('[DEBUG COMBO SERVICE] getAll returning', result.length, 'combos, first:', result[0] ? result[0].name : 'none');
    return result;
  }

  async update(id, updateData, token) {
    console.log('[DEBUG COMBO SERVICE] update called for id:', id, 'data:', updateData);
    const updated = await super.update(id, updateData, token);
    // Cascading: update stock or related currencies
    if (updateData.price) {
      // Simulate recalculate totalPrice
      updated.totalPrice = updated.price + updated.deliveryFee;
    }
    console.log('[DEBUG COMBO SERVICE] update returning updated combo');
    return updated;
  }
}

// Currency Service
export class CurrencyService extends (isDemoMode() ? BaseMockService : BaseRealService) {
  constructor() {
    super(isDemoMode() ? 'currencies' : 'currencies');
  }

  handleCascadingUpdates(currency) {
    // Update rates in related combos/receivers if changed
    if (initialMockData.combos) {
      initialMockData.combos.forEach(combo => {
        if (combo.currencyId === currency._id) {
          // Simulate rate update effect
          combo.rate = currency.rateToCup;
        }
      });
    }
  }
}

// Receiver Service
export class ReceiverService extends (isDemoMode() ? BaseMockService : BaseRealService) {
  constructor() {
    super(isDemoMode() ? 'receivers' : 'receivers');
  }

  async create(newReceiver, token) {
    const created = await super.create(newReceiver, token);
    // Cascading: add to user's receivers array
    const userId = newReceiver.userId;
    const userService = new UserService();
    const user = await userService.getById(userId);
    if (!user.receivers) user.receivers = [];
    user.receivers.push(created._id);
    await userService.update(userId, user);
    return created;
  }

  handleCascadingDeletes(id) {
    // Remove from user's receivers
    initialMockData.users.forEach(user => {
      if (user.receivers) {
        user.receivers = user.receivers.filter(rId => rId !== id);
      }
    });
  }
}

// Factory to create services
export const createService = (serviceType) => {
  switch (serviceType) {
    case 'auth':
      return new AuthService();
    case 'users':
      return new UserService();
    case 'combos':
      return new ComboService();
    case 'currencies':
      return new CurrencyService();
    case 'receivers':
      return new ReceiverService();
    default:
      throw new Error(`Unknown service type: ${serviceType}`);
  }
};

// Export for easy import
export default createService;